import apiClient from './apiClient';

// Types for patients data
export interface Patient {
  id: string;
  name: string;
  species: string;
  breed: string;
  sex: 'male' | 'female';
  age: number;
  weight?: number;
  color?: string;
  microchip?: string;
  dateOfBirth?: string;
  status: 'active' | 'deceased' | 'transferred';
  clientId: string;
  clientName: string;
  tags: string[];
  allergies: string[];
  temperament?: string;
  sensitivities?: string[];
  createdAt: string;
  updatedAt: string;
  clinicId: string;
  // Computed fields
  lastVisit?: string;
  visitCount: number;
  vaccinationStatus: 'up_to_date' | 'overdue' | 'unknown';
  nextVaccinationDue?: string;
}

export interface PatientFilters {
  search?: string;
  species?: string;
  breed?: string;
  sex?: 'male' | 'female' | 'all';
  status?: 'active' | 'deceased' | 'transferred' | 'all';
  ageMin?: number;
  ageMax?: number;
  clientId?: string;
  tags?: string[];
  vaccinationStatus?: 'up_to_date' | 'overdue' | 'unknown' | 'all';
  clinicId?: string;
  page?: number;
  limit?: number;
  sortBy?: 'name' | 'species' | 'age' | 'lastVisit' | 'visitCount';
  sortOrder?: 'asc' | 'desc';
}

export interface PatientDetail extends Patient {
  client: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  medicalHistory: Array<{
    id: string;
    date: string;
    type: 'consultation' | 'vaccination' | 'surgery' | 'treatment' | 'checkup';
    veterinarian: string;
    diagnosis?: string;
    treatment?: string;
    notes?: string;
    documents?: Array<{
      id: string;
      name: string;
      type: string;
      url: string;
    }>;
  }>;
  vaccinations: Array<{
    id: string;
    type: string;
    date: string;
    nextDue: string;
    veterinarian: string;
    certificate?: string;
    isOverdue: boolean;
  }>;
  surgeries: Array<{
    id: string;
    name: string;
    date: string;
    veterinarian: string;
    materials: string[];
    risk: 'low' | 'medium' | 'high';
    consent: boolean;
    notes?: string;
  }>;
  appointments: Array<{
    id: string;
    date: string;
    time: string;
    service: string;
    veterinarian: string;
    status: 'scheduled' | 'confirmed' | 'present' | 'no-show' | 'cancelled';
    notes?: string;
  }>;
  documents: Array<{
    id: string;
    name: string;
    type: 'image' | 'xray' | 'analysis' | 'certificate' | 'other';
    uploadDate: string;
    fileSize: number;
    url: string;
    description?: string;
  }>;
  measurements: Array<{
    id: string;
    date: string;
    weight?: number;
    temperature?: number;
    painScore?: number;
    notes?: string;
  }>;
  notes: Array<{
    id: string;
    content: string;
    author: string;
    createdAt: string;
    type: 'temperament' | 'sensitivity' | 'general';
  }>;
  timeline: Array<{
    id: string;
    type: 'appointment' | 'vaccination' | 'surgery' | 'measurement' | 'document' | 'note';
    title: string;
    description: string;
    date: string;
    metadata?: any;
  }>;
}

export interface CreatePatientData {
  name: string;
  species: string;
  breed: string;
  sex: 'male' | 'female';
  age: number;
  weight?: number;
  color?: string;
  microchip?: string;
  dateOfBirth?: string;
  ownerId: string; // Backend expects ownerId, not clientId
  tags: string[];
  allergies: string[];
  temperament?: string;
  sensitivities?: string[];
  // Informatii despre proprietar pentru crearea automata
  ownerName?: string;
  ownerPhone?: string;
  ownerEmail?: string;
}

export interface UpdatePatientData extends Partial<CreatePatientData> {
  status?: 'active' | 'deceased' | 'transferred';
}

export interface PatientStats {
  total: number;
  active: number;
  deceased: number;
  transferred: number;
  bySpecies: Array<{
    species: string;
    count: number;
  }>;
  vaccinationOverdue: number;
  averageVisitsPerPatient: number;
}

export interface TransferPatientData {
  newClientId: string;
  reason: string;
  transferDate: string;
}

export interface MarkDeceasedData {
  date: string;
  cause?: string;
  notes?: string;
}

class PatientsService {
  async getPatients(filters: PatientFilters = {}): Promise<{
    patients: Patient[];
    total: number;
    page: number;
    limit: number;
    stats: PatientStats;
  }> {
    try {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.species) params.append('species', filters.species);
      if (filters.breed) params.append('breed', filters.breed);
      if (filters.sex && filters.sex !== 'all') params.append('sex', filters.sex);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.ageMin !== undefined) params.append('ageMin', filters.ageMin.toString());
      if (filters.ageMax !== undefined) params.append('ageMax', filters.ageMax.toString());
      if (filters.clientId) params.append('clientId', filters.clientId);
      if (filters.tags && filters.tags.length > 0) params.append('tags', filters.tags.join(','));
      if (filters.vaccinationStatus && filters.vaccinationStatus !== 'all') params.append('vaccinationStatus', filters.vaccinationStatus);
      if (filters.clinicId) params.append('clinicId', filters.clinicId);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

      const response = await apiClient.get(`/patients?${params}`);

      // Transform backend response to match expected interface
      const backendData = response.data.data;
      return {
        patients: backendData.patients || [],
        total: backendData.pagination?.total || 0,
        page: backendData.pagination?.page || 1,
        limit: backendData.pagination?.limit || 10,
        stats: backendData.stats || {
          total: 0,
          active: 0,
          inactive: 0,
          bySpecies: {},
          byGender: {},
          averageAge: 0
        }
      };
    } catch (error) {
      console.error('Error fetching patients:', error);
      return {
        patients: [],
        total: 0,
        page: 1,
        limit: 10,
        stats: {
          total: 0,
          active: 0,
          deceased: 0,
          transferred: 0,
          bySpecies: [],
          vaccinationOverdue: 0,
          averageVisitsPerPatient: 0
        }
      };
    }
  }

  async getPatientById(id: string): Promise<PatientDetail | null> {
    try {
      const response = await apiClient.get(`/patients/${id}`);

      return response.data;
    } catch (error) {
      console.error('Error fetching patient details:', error);
      return null;
    }
  }

  async createPatient(patientData: CreatePatientData): Promise<Patient | null> {
    try {
      const response = await apiClient.post('/patients', patientData);

      if (response.data.success) {
        return response.data.data;
      } else {
        console.error('Backend error:', response.data.error);
        return null;
      }
    } catch (error) {
      console.error('Error creating patient:', error);
      return null;
    }
  }

  async updatePatient(id: string, patientData: UpdatePatientData): Promise<Patient | null> {
    try {
      const response = await apiClient.put(`/patients/${id}`, patientData);

      return response.data;
    } catch (error) {
      console.error('Error updating patient:', error);
      return null;
    }
  }

  async deletePatient(id: string): Promise<boolean> {
    try {
      await apiClient.delete(`/patients/${id}`);

      return true;
    } catch (error) {
      console.error('Error deleting patient:', error);
      return false;
    }
  }

  async transferPatient(id: string, transferData: TransferPatientData): Promise<boolean> {
    try {
      await apiClient.post(`/patients/${id}/transfer`, transferData);

      return true;
    } catch (error) {
      console.error('Error transferring patient:', error);
      return false;
    }
  }

  async markDeceased(id: string, deceasedData: MarkDeceasedData): Promise<boolean> {
    try {
      await apiClient.post(`/patients/${id}/deceased`, deceasedData);

      return true;
    } catch (error) {
      console.error('Error marking patient as deceased:', error);
      return false;
    }
  }

  async addVaccination(patientId: string, vaccinationData: {
    type: string;
    date: string;
    veterinarian: string;
    certificate?: string;
  }): Promise<boolean> {
    try {
      await apiClient.post(`/patients/${patientId}/vaccinations`, vaccinationData);

      return true;
    } catch (error) {
      console.error('Error adding vaccination:', error);
      return false;
    }
  }

  async addDocument(patientId: string, documentData: {
    name: string;
    type: 'image' | 'xray' | 'analysis' | 'certificate' | 'other';
    file: File;
    description?: string;
  }): Promise<boolean> {
    try {
      const formData = new FormData();
      formData.append('name', documentData.name);
      formData.append('type', documentData.type);
      formData.append('file', documentData.file);
      if (documentData.description) {
        formData.append('description', documentData.description);
      }

      await apiClient.post(`/patients/${patientId}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return true;
    } catch (error) {
      console.error('Error adding document:', error);
      return false;
    }
  }

  async addMeasurement(patientId: string, measurementData: {
    weight?: number;
    temperature?: number;
    painScore?: number;
    notes?: string;
  }): Promise<boolean> {
    try {
      await apiClient.post(`/patients/${patientId}/measurements`, measurementData);

      return true;
    } catch (error) {
      console.error('Error adding measurement:', error);
      return false;
    }
  }

  async addNote(patientId: string, noteData: {
    content: string;
    type: 'temperament' | 'sensitivity' | 'general';
  }): Promise<boolean> {
    try {
      await apiClient.post(`/patients/${patientId}/notes`, noteData);

      return true;
    } catch (error) {
      console.error('Error adding note:', error);
      return false;
    }
  }

  async createAppointment(patientId: string, appointmentData: {
    date: string;
    time: string;
    service: string;
    veterinarian: string;
    notes?: string;
  }): Promise<{ appointmentId: string } | null> {
    try {
      const response = await apiClient.post(`/patients/${patientId}/appointments`, appointmentData);

      return response.data;
    } catch (error) {
      console.error('Error creating appointment:', error);
      return null;
    }
  }

  async exportPatients(filters: PatientFilters, format: 'csv' | 'xlsx'): Promise<Blob | null> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
      params.append('format', format);

      const response = await apiClient.get(`/patients/export?${params}`, {
        responseType: 'blob'
      });

      return response.data;
    } catch (error) {
      console.error('Error exporting patients:', error);
      return null;
    }
  }

  async getAvailableSpecies(): Promise<Array<{ value: string; label: string }>> {
    try {
      const response = await apiClient.get('/patients/species');

      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching available species:', error);
      return [];
    }
  }

  async getBreedsBySpecies(species: string): Promise<string[]> {
    try {
      const response = await apiClient.get(`/patients/breeds?species=${species}`);

      return response.data.breeds || [];
    } catch (error) {
      console.error('Error fetching breeds:', error);
      return [];
    }
  }

  async getAvailableTags(): Promise<Array<{ value: string; label: string }>> {
    try {
      const response = await apiClient.get('/patients/tags');

      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching available tags:', error);
      return [];
    }
  }

  async getVaccinationDue(): Promise<Array<{
    patientId: string;
    patientName: string;
    clientName: string;
    vaccinationType: string;
    dueDate: string;
    daysOverdue: number;
  }>> {
    try {
      const response = await apiClient.get('/patients/vaccinations-due');

      return response.data.vaccinations || [];
    } catch (error) {
      console.error('Error fetching vaccination due:', error);
      return [];
    }
  }
}

export const patientsService = new PatientsService();
